import useNotificationStore from '@gui/store/notification';
import { AvailableGeodata, NotificationType } from '@visualizer/types';
import { AxiosInstance } from 'axios';
import { LoadingStatus, patchDownloadQueueItem, setLoadingStatus } from 'store/LoadingStore';
import { checkKey, getArrayBuffer, setArrayBuffer } from './IndexedDB_Handler';
import { ApiRoutes } from 'store/ApiRoutes';
import axios from 'axios';

const getFileTooLargeMessage = (fileName: string) => `The software crashed because the file is too large for layer ${fileName}`;

function isFileTooLargeOrMemoryError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    const message = error.message.toLowerCase();
    return (
        error.name === 'QuotaExceededError' ||
        error.name === 'RangeError' ||
        message.includes('array buffer allocation failed') ||
        message.includes('out of memory') ||
        message.includes('invalid array length') ||
        message.includes('quota') ||
        message.includes('file is too large')
    );
}

function raiseFatalFileTooLargeError(cause: unknown, fileName: string): Error {
    const message = getFileTooLargeMessage(fileName);
    const normalizedError = cause instanceof Error ? cause : new Error(String(cause));
    console.error('[FileFetcher] File too large / memory failure', normalizedError);
    useNotificationStore.getState().showNotification(message, NotificationType.Error, true, true);
    setLoadingStatus(LoadingStatus.ERROR);
    return new Error(message, { cause: normalizedError });
}

export class FileFetcherV2 {
    private apiClient: AxiosInstance;
    private static readonly PROGRESS_THROTTLE_MS = 100;

    constructor() {
        this.apiClient = axios.create({
            baseURL: `/api/`,
            headers: {
                'Content-Type': 'application/json',
            },
            withCredentials: true,
            timeout: 120000,
        });
    }

    private getAxiosHeaders(): Record<string, string> {
        const headers = this.apiClient.defaults.headers as unknown as Record<string, unknown>;
        const collected: Record<string, string> = {};

        const appendHeaders = (source: unknown) => {
            if (!source || typeof source !== 'object') return;
            for (const [key, value] of Object.entries(source)) {
                if (typeof value === 'string') {
                    collected[key] = value;
                }
            }
        };

        appendHeaders(headers.common);
        appendHeaders(headers.get);
        appendHeaders(headers);
        return collected;
    }

    private resolveEndpoint(endpoint: string): string {
        const baseURL = this.apiClient.defaults.baseURL ?? '';
        if (/^https?:\/\//i.test(endpoint)) return endpoint;

        if (typeof window !== 'undefined') {
            const base = new URL(baseURL || '/', window.location.origin);
            return new URL(endpoint, base).toString();
        }

        return `${baseURL}${endpoint}`;
    }

    private async downloadCompressedFile(key: string, endpoint: string, trackProgress: boolean): Promise<ArrayBuffer> {
        const url = this.resolveEndpoint(endpoint);
        if (trackProgress) {
            patchDownloadQueueItem(key, {
                label: endpoint,
                status: 'queued',
                loadedBytes: 0,
                totalBytes: null,
                rateBytesPerSecond: 0,
                remainingSeconds: null,
            });
        }

        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAxiosHeaders(),
            credentials: this.apiClient.defaults.withCredentials ? 'include' : 'same-origin',
        });
        if (response.status === 404) {
            throw new Error(`File not found at ${endpoint} (status 404)`);
        }

        if (!response.ok) {
            throw new Error(`Network response was not ok for ${endpoint} (status ${response.status})`);
        }
        if (trackProgress) {
            patchDownloadQueueItem(key, {
                label: endpoint,
                status: 'downloading',
                loadedBytes: 0,
                totalBytes: Number(response.headers.get('content-length') ?? 0) || null,
                rateBytesPerSecond: 0,
                remainingSeconds: null,
            });
        }
        if (!response.body) {
            const data = await response.arrayBuffer();
            if (trackProgress) {
                patchDownloadQueueItem(key, {
                    status: 'completed',
                    loadedBytes: data.byteLength,
                    totalBytes: data.byteLength,
                    rateBytesPerSecond: 0,
                    remainingSeconds: 0,
                });
            }
            return data;
        }

        return this.streamWithProgress(key, endpoint, response, trackProgress);
    }

    public primeDownloadQueue(items: Array<{ key: string; endpoint: string; label?: string }>) {
        items.forEach((item) => {
            patchDownloadQueueItem(item.key, {
                label: item.label ?? item.endpoint,
                status: 'queued',
                loadedBytes: 0,
                totalBytes: null,
                rateBytesPerSecond: 0,
                remainingSeconds: null,
            });
        });
    }

    private async streamWithProgress(key: string, endpoint: string, response: Response, trackProgress: boolean): Promise<ArrayBuffer> {
        const contentLength = Number(response.headers.get('content-length') ?? 0);
        const totalBytes = Number.isFinite(contentLength) && contentLength > 0 ? contentLength : null;
        const reader = response.body!.getReader();
        const chunks: Uint8Array[] = [];
        const startedAt = Date.now();
        let loadedBytes = 0;
        let lastNotify = 0;
        let lastRateBytesPerSecond = 0;

        if (trackProgress) {
            patchDownloadQueueItem(key, {
                label: endpoint,
                status: 'downloading',
                loadedBytes: 0,
                totalBytes,
                rateBytesPerSecond: 0,
                remainingSeconds: null,
            });
        }

        try {
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                if (!value) continue;

                chunks.push(value);
                loadedBytes += value.byteLength;

                const now = Date.now();
                const elapsedSeconds = Math.max((now - startedAt) / 1000, 0.001);
                const rateBytesPerSecond = loadedBytes / elapsedSeconds;
                const remainingSeconds = totalBytes ? Math.max((totalBytes - loadedBytes) / Math.max(rateBytesPerSecond, 1), 0) : null;
                lastRateBytesPerSecond = rateBytesPerSecond;
                if (trackProgress && (lastNotify === 0 || now - lastNotify >= FileFetcherV2.PROGRESS_THROTTLE_MS)) {
                    patchDownloadQueueItem(key, {
                        status: 'downloading',
                        loadedBytes,
                        totalBytes,
                        rateBytesPerSecond,
                        remainingSeconds,
                    });
                    lastNotify = now;
                }
            }
        } finally {
            reader.releaseLock();
        }

        const totalLength = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
        const output = new Uint8Array(totalLength);
        let offset = 0;
        for (const chunk of chunks) {
            output.set(chunk, offset);
            offset += chunk.byteLength;
        }

        if (trackProgress) {
            patchDownloadQueueItem(key, {
                status: 'completed',
                loadedBytes: totalLength,
                totalBytes: totalBytes ?? totalLength,
                rateBytesPerSecond: lastRateBytesPerSecond,
                remainingSeconds: 0,
            });
        }

        return output.buffer;
    }

    public async downloadFile(key: string, endpoint: string, options?: { trackProgress?: boolean }): Promise<void> {
        const trackProgress = options?.trackProgress ?? true;
        // 1. Check if indexed db contains this file and if it does, return
        if (await checkKey(key)) {
            if (trackProgress) {
                patchDownloadQueueItem(key, {
                    label: endpoint,
                    status: 'cached',
                    loadedBytes: 0,
                    totalBytes: 0,
                    rateBytesPerSecond: 0,
                    remainingSeconds: 0,
                });
            }
            return;
        }

        try {
            const arrayBuffer = await this.downloadCompressedFile(key, endpoint, trackProgress);
            await setArrayBuffer(key, arrayBuffer);
            return;
        } catch (error) {
            // Surface a consistent error for quota / memory issues.
            if (isFileTooLargeOrMemoryError(error)) {
                if (trackProgress) {
                    patchDownloadQueueItem(key, {
                        label: endpoint,
                        status: 'error',
                        error: error instanceof Error ? error.message : String(error),
                    });
                }
                throw raiseFatalFileTooLargeError(error, endpoint);
            }

            const normalizedError = error instanceof Error ? error : new Error(String(error));
            console.error(`[FileFetcher] Failed to fetch file from ${endpoint}:`, normalizedError);
            if (trackProgress) {
                patchDownloadQueueItem(key, {
                    label: endpoint,
                    status: 'error',
                    error: normalizedError.message,
                });
            }
            throw new Error(`Download failed for ${endpoint}: ${normalizedError.message}`, { cause: normalizedError });
        }
    }

    public async getFile(key: string) {
        if (!(await checkKey(key))) {
            throw new Error(`File with key ${key} not found in IndexedDB`);
        }
        return await getArrayBuffer(key);
    }

    public async fetchResults(simSetID: string, subSimID: number): Promise<number> {
        const response = await this.apiClient.get<any>(ApiRoutes.resultsList(simSetID, subSimID));
        return response.data.length;
    }

    public async availableGeodata(geodataId: string) {
        if (!ApiRoutes.canUseApi()) {
            return { ground: true, buildings: true, vegetation: true, roads: true, target: true } satisfies AvailableGeodata;
        }

        const response = await this.apiClient.get<{
            terrain?: unknown | null;
            building?: unknown | null;
            vegetation?: unknown | null;
        }>(`geodatas/${geodataId}`);

        return {
            ground: Boolean(response.data.terrain),
            buildings: Boolean(response.data.building),
            vegetation: Boolean(response.data.vegetation),
            roads: true,
            target: true,
        } satisfies AvailableGeodata;
    }

    public async fetchContentLength(endpoint: string): Promise<number | null> {
        const url = this.resolveEndpoint(endpoint);
        const response = await fetch(url, {
            method: 'GET',
            headers: this.getAxiosHeaders(),
            credentials: this.apiClient.defaults.withCredentials ? 'include' : 'same-origin',
        });
        if (!response.ok) return null;

        const contentLength = Number(response.headers.get('content-length') ?? 0);
        const knownLength = Number.isFinite(contentLength) && contentLength > 0 ? contentLength : null;

        if (response.body) {
            try {
                const reader = response.body.getReader();
                await reader.cancel();
            } catch {
                // Ignore: best-effort to stop transfer after reading headers.
            }
        }

        return knownLength;
    }
}
